import Cookies from 'js-cookie';

const COOKIE_CONSENT_COOKIE_NAME = 'cookies-consent';
const COOKIE_CONSENT_VERSION = 2;
const COOKIE_CONSENT_EXPIRATION = 365;

export const COOKIE_CONSENT_OPEN_EVENT = 'openConsentCookieModal';

export type ConsentKey =
  | 'adStorage'
  | 'adUserData'
  | 'adPersonalization'
  | 'analyticsStorage'
  | 'functionalityStorage'
  | 'personalizationStorage'
  | 'securityStorage';

export type ConsentSignal = 'granted' | 'denied';

/** Choix brut de l'utilisateur (bannière ou switchs de la modale), avant traduction en signaux. */
export type ConsentChoice = Record<ConsentKey, boolean>;

/**
 * Forme exacte du JSON écrit dans le cookie `cookies-consent`. C'est un contrat partagé avec le
 * head Twirl de `bluevalet-server` (`gtm/consent.scala.js`), qui relit ce cookie avant `gtm.js`.
 */
export type ConsentState = {
  version: number;
  status: 'accepted' | 'declined';
  details: Record<ConsentKey, ConsentSignal>;
};

const toSignal = (granted: boolean): ConsentSignal => (granted ? 'granted' : 'denied');

// Un cookie écrit par une version antérieure est ignoré : le consentement est redemandé.
const parseConsentState = (cookie: string): ConsentState | null => {
  try {
    const state = JSON.parse(cookie) as ConsentState;
    return state.version >= COOKIE_CONSENT_VERSION ? state : null;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error while parsing cookie');
    return null;
  }
};

export const getCookieConsentStateFromCookie = (): ConsentState | null => {
  const cookie = Cookies.get(COOKIE_CONSENT_COOKIE_NAME);

  return cookie ? parseConsentState(cookie) : null;
};

export const setCookieConsentStateInCookie = (state: ConsentState): void => {
  Cookies.set(COOKIE_CONSENT_COOKIE_NAME, JSON.stringify(state), {
    expires: COOKIE_CONSENT_EXPIRATION,
  });
};

export const generateCookieContentState = (consent: ConsentChoice): ConsentState => ({
  version: COOKIE_CONSENT_VERSION,
  status: Object.values(consent).some(granted => granted) ? 'accepted' : 'declined',
  details: {
    adStorage: toSignal(consent.adStorage),
    adUserData: toSignal(consent.adUserData),
    adPersonalization: toSignal(consent.adPersonalization),
    analyticsStorage: toSignal(consent.analyticsStorage),
    functionalityStorage: toSignal(consent.functionalityStorage),
    personalizationStorage: toSignal(consent.personalizationStorage),
    securityStorage: toSignal(consent.securityStorage),
  },
});

export const isGranted = (state: ConsentSignal | undefined): boolean => state === 'granted';
